import React from 'react';

import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { LinkPickerState, LinkSearchListItemData } from '../../../../common/types';
import { checkSubmitDisabled } from './utils';

type LinkPickerButtonGroupProps = {
	isEditing?: boolean;
	isLoading: boolean;
	isSubmitting: boolean;
	customSubmitButtonLabel?: MessageDescriptor;
	error: unknown | null;
	items: LinkSearchListItemData[] | null;
	queryState: LinkPickerState | null;
	submitMessageId?: string;
	testId?: string;
	url: string;
	disableManualUrlInsert?: boolean;
};

export const messages: {
	saveButton: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	insertButton: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	saveButton: {
		id: 'fabric.linkPicker.button.save',
		defaultMessage: 'Save',
		description:
			'Label for the submit button in the link picker form footer when editing an existing link, saves the updated link',
	},
	insertButton: {
		id: 'fabric.linkPicker.button.insert',
		defaultMessage: 'Insert',
		description: 'Button to insert searched or selected link',
	},
});

export const LinkPickerSubmitButton = ({
	isEditing,
	isLoading,
	isSubmitting,
	customSubmitButtonLabel,
	error,
	items,
	queryState,
	submitMessageId,
	testId,
	url,
	disableManualUrlInsert,
}: LinkPickerButtonGroupProps): React.JSX.Element => {
	const intl = useIntl();
	const insertButtonMsg = isEditing ? messages.saveButton : messages.insertButton;

	const isSubmitDisabled = checkSubmitDisabled(
		isLoading,
		isSubmitting,
		error,
		url,
		queryState,
		items,
		fg('add-disable-manual-url-capability-technical') ? disableManualUrlInsert : undefined,
	);

	return (
		<Button
			type="submit"
			appearance="primary"
			testId={testId}
			isDisabled={isSubmitDisabled}
			aria-labelledby={isSubmitting ? submitMessageId : undefined}
			isLoading={isSubmitting}
		>
			{customSubmitButtonLabel
				? intl.formatMessage(customSubmitButtonLabel)
				: intl.formatMessage(insertButtonMsg)}
		</Button>
	);
};
